'use client';
import {
  getPageUrlWithoutQueryParams,
  hasValue,
  redirectTo,
} from '@r-and-a-shared-ui/utils';
import {
  buildCustomOauthState,
  clearForceAuthParams,
  getBearerToken,
  getOauthChangePasswordReturnUrl,
  getOauthDeleteReturnUrl,
  getOauthFlowReturnUrl,
  getOauthRegisterReturnUrl,
  getOauthSignInReturnUrl,
  getOauthSignOutReturnUrl,
  getOauthUpdateReturnUrl,
  getUserCreatedDateUrl,
  handleSignIn,
  handleSignOut,
  handleUpgrade,
  setOauthFlowReturnUrl,
  getRedirectUriPath,
  getUserReferralCodeUrl,
} from '../utils/auth-helpers';
import {
  signInWithRedirect,
  signOut,
  fetchAuthSession,
} from 'aws-amplify/auth';
import { AuthConfig, AuthTokens } from '@aws-amplify/core';
import { UserJourneyTypes } from '../types';
import axios from 'axios';

export const handleOauthSignedOut = () => {
  const redirectUri = getOauthSignOutReturnUrl(true);

  if (
    getPageUrlWithoutQueryParams().toString() !== redirectUri?.toLowerCase()
  ) {
    redirectTo(redirectUri);
  }
};

export const handleOauthSignedIn = () => {
  let redirectUri = getOauthSignInReturnUrl(true);
  const registerRedirectUri = getOauthRegisterReturnUrl();

  if (registerRedirectUri) {
      redirectUri += registerRedirectUri;
  }

  if (
    getPageUrlWithoutQueryParams().toString().toLowerCase() !==
    redirectUri?.toLowerCase()
  ) {
    redirectTo(redirectUri);
  }
};

export const federatedSignIn = async (customState?: string, forceSignOut = true) => {
  try {
    fetchAuthSession()
      .then(authSession => {
        clearForceAuthParams();
        if (!authSession?.tokens) {
          amplifySignInWithRedirect(customState);
        }
      })
      .catch(() => {
        amplifySignInWithRedirect(customState);
      });
  } catch (error) {
    console.error('Error signing in:', error);
  } finally {
    if (!forceSignOut) {
      return;
    }

    await signOut();
    amplifySignInWithRedirect(customState);
  }
};

const amplifySignInWithRedirect = (customState?: string) => {
  handleSignIn();
  if (hasValue(customState)) {
    signInWithRedirect({ customState });
  } else {
    signInWithRedirect();
  }
};

export const federatedSignOut = async (global = false) => {
  try {
    handleSignOut();
    signOut({ global });
  } catch (error) {
    console.error('Error signing out:', error);
  }
};

export const signUp = (
  userJourneyType?: UserJourneyTypes,
  forceSignOut = true,
) => {
  try {
    setOauthFlowReturnUrl();
    const registerRoute = process.env.NEXT_PUBLIC_MEMBER_REGISTER_ROUTE || 'member-register';
    const redirectUriPath = getRedirectUriPath();
    const customState = buildCustomOauthState(
      registerRoute,
      userJourneyType,
      redirectUriPath);

    federatedSignIn(customState, forceSignOut);
  } catch (error) {
    console.error(error);
  }
};

export const updateAccountData = (
  config: AuthConfig,
  userJourneyType?: UserJourneyTypes,
) => {
  try {
    const targetUrl = getOauthUpdateReturnUrl(config, userJourneyType);

    redirectTo(targetUrl);
  } catch (error) {
    console.error(error);
  }
};

export const upgradeAccount = (
  config: AuthConfig,
  userJourneyType?: UserJourneyTypes,
) => {
  try {
    handleUpgrade(config, userJourneyType);
  } catch (error) {
    console.error(error);
  }
};

export const fetchTokens = async (): Promise<AuthTokens> => {
  try {
    const tokens = (await fetchAuthSession()).tokens ?? ({} as AuthTokens);
    return tokens;
  } catch (err) {
    console.log(err);
    return {} as AuthTokens;
  }
};

export const handleOauthCompleteFlow = () => {
  const redirectUri = getOauthFlowReturnUrl() || window.location.origin;

  if (!hasValue(redirectUri)) {
    return;
  }

  redirectTo(redirectUri);
};

export const changePassword = (
  config: AuthConfig,
  userJourneyType?: UserJourneyTypes,
) => {
  try {
    setOauthFlowReturnUrl();
    const targetUrl = getOauthChangePasswordReturnUrl(config, userJourneyType);

    redirectTo(targetUrl);
  } catch (error) {
    console.error(error);
  }
};

export const deleteAccount = (config: AuthConfig, username?: string) => {
  try {
    const targetUrl = getOauthDeleteReturnUrl(config, username);

    redirectTo(targetUrl);
  } catch (error) {
    console.error(error);
  }
};

export const fetchCreatedDate = async (
  config: AuthConfig,
  username?: string,
) => {
  try {
    const url = getUserCreatedDateUrl(config);
    const bearerToken = await getBearerToken();
    if (!bearerToken || !url) {
      return null;
    }

    const createdDate = await axios.get(url, {
      params: {
        username,
      },
      headers: {
        Accept: 'application/json',
        'Cache-Control': 'no-cache',
        Authorization: bearerToken,
      },
    });
    return createdDate;
  } catch (err) {
    return null;
  }
};

export const fetchReferralCode = async (
  config: AuthConfig,
  username?: string,
) => {
  try {
    const url = getUserReferralCodeUrl(config);
    const bearerToken = await getBearerToken();
    if (!bearerToken || !url) {
      return null;
    }

    const createdDate = await axios.get(url, {
      params: {
        username,
      },
      headers: {
        Accept: 'application/json',
        'Cache-Control': 'no-cache',
        Authorization: bearerToken,
      },
    });
    return createdDate;
  } catch (err) {
    return null;
  }
};
